import { useState, useMemo } from 'react'
import { useMindmaps } from '../hooks/useMindmaps'
import { ProfileService } from '../services/profile/profileService'
import { DashboardHeader, type ProjectTab } from '../components/Dashboard/DashboardHeader'
import { MindmapTable } from '../components/Dashboard/MindmapTable'
import { CreateProjectModal } from '../components/Dashboard/CreateProjectModal'
import { UpgradePromptModal } from '../components/UpgradePromptModal'
import { DashboardContainer } from './styles/DashboardPageStyles'
import { SkeletonDashboard } from '../features/skeleton'

export function DashboardPage() {
  const { mindmaps, loading, loadMindmaps } = useMindmaps()

  // UI State
  const [searchText, setSearchText] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [activeTab, setActiveTab] = useState<ProjectTab>('active')

  // Filter mindmaps by active/archived tab
  const { filteredMindmaps, archivedCount } = useMemo(() => {
    const active = mindmaps.filter((m) => !m.is_archived)
    const archived = mindmaps.filter((m) => m.is_archived)
    return {
      filteredMindmaps: activeTab === 'active' ? active : archived,
      archivedCount: archived.length,
    }
  }, [mindmaps, activeTab])

  const handleCreateProject = async () => {
    const { data } = await ProfileService.getProjectQuota()
    if (data && !data.canCreate && !data.isUnlimited) {
      setShowUpgradeModal(true)
    } else {
      setShowCreateModal(true)
    }
  }

  if (loading) return <SkeletonDashboard />

  return (
    <DashboardContainer>
      <DashboardHeader
        searchText={searchText}
        onSearchChange={setSearchText}
        onCreateProject={() => void handleCreateProject()}
        hasProjects={mindmaps.length > 0}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        archivedCount={archivedCount}
      />

      <MindmapTable
        mindmaps={filteredMindmaps}
        loading={loading}
        searchText={searchText}
        loadMindmaps={loadMindmaps}
        onCreateProject={() => void handleCreateProject()}
        isArchivedView={activeTab === 'archived'}
      />

      <CreateProjectModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onUpgradeClick={() => setShowUpgradeModal(true)}
      />

      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        featureName="Unlimited projects"
      />
    </DashboardContainer>
  )
}
